import { Logger, Log } from "../Logger";
import { NewformaApiClient } from "./NewformaApiClient";
import { LogRfiParams } from "../../models/workflow/rfi/LogRfiParams";
import { LogRfiResponse } from "../../models/workflow/rfi/LogRfiResponse";
import { Request } from "aws-sign-web";
import { HttpRequestWrapper } from "../HttpRequestWrapper";
import { ExpiredSessionError } from "../../models/ExpiredSessionError";
import { ApiRequestErrorLevel, ApiRequestErrorWithMessage } from "../../models/ApiRequestErrorWithMessage";
import { AnalyticsActionType, AnalyticsCategoryType, AnalyticsManager } from "../AnalyticsManager";
import { FileWithId } from "../../models/shared/FileWithId";
import AttachmentDetails = Office.AttachmentDetails;
import { AttachmentDetailsMetadata } from "../../models/AttachmentDetailsMetadata";
import { OutlookApiService } from "../OutlookApiService";
import { FileUploadApiService } from "./FileUploadApiService";
import v4 from "uuid/v4";
import { ForwardedRfi, RfiResponse } from "../../models/workflow/rfi/RfiResponse";
import { ProjectKeywordsResponse } from "../../models/ProjectKeywordsResponse";
import { WorkflowActionResponse } from "../../models/workflow/WorkflowActionResponse";
import { RfiReviewResponseRequestParams } from "../../models/workflow/rfi/RfiReviewResponseRequestParams";
import { RfiDetailsResponse } from "../../models/workflow/rfi/RfiDetailsResponse";
import { WorkflowActionType } from "../../models/workflow/WorkflowActionType";
import { EmailApiService } from "./EmailApiService";
import { NrnServiceWrapper } from "../NrnServiceWrapper";
import { PostEmailFilingResponse } from "../../models/PostEmailFilingResponse";
import { AttachmentItem } from "../../models/shared/AttachmentList";
import { mapAttachmentsMetadataToAttachment } from "../../helpers/SendAndFile/AttachmentHelpers";
import { LogForwardRfiRequest } from "../../models/workflow/rfi/ForwardRfiRequest";
import { LogForwardRfiResponse } from "../../models/workflow/rfi/ForwardRfiResponse";
import { LocalStorageKeys as LS } from "../../models/StorageKeys";
import { DateHelper } from "../../helpers/DateHelpers";

const CACHED_RFIS = LS.cachedRfis;
const MAX_RFIS = LS.maxRfisPageSize;
const CACHE_DISABLED = LS.cacheDisabled;

let subApi: any;

export class RfiApiService {
    componentName = "RfiApiService";
    private abortCount: number = 0;
    private lock = false;
    private _urlRoot: string = "";
    private maxRetries = 3;
    private retryDelay = 5000; // 5 seconds

    private _wasCached = false;
    private _cacheServer = "";
    private _rifs: any = [];
    private _expiredRfisMs = 5 * 60 * 1000; // 5 minutes
    private maxRfisPageSize = 400;

    constructor(
        private logger: Logger,
        private newformaApiClient: NewformaApiClient,
        private requestWrapper: HttpRequestWrapper,
        private analyticsManager: AnalyticsManager,
        private outlookApiService: OutlookApiService,
        private fileUploadApiService: FileUploadApiService,
        private emailApiService: EmailApiService,
        private nrnServiceWrapper: NrnServiceWrapper
    ) {
        Log.infoStyle(`RfiApiService Id=${Math.round(Math.random() * 10000)}`, "red");
        subApi = this;

        const cacheRfis = localStorage.getItem(CACHED_RFIS);
        if (cacheRfis) {
            try {
                this._wasCached = true;

                this._rifs = JSON.parse(cacheRfis);

                const first = this._rifs[0]?.items[0]?.key;
                this._cacheServer = this.getNPCServer(first);
                Log.info(`Cached from server ${this._cacheServer}`);
                // make sure nothing too old is used.
                this.purgeCacheRfis();
            } catch {}
        } else {
            // no external cache make sure the memory cache is also empty
            this._rifs = [];
        }
        // load allowed page size if existing.
        const maxPageSize = localStorage.getItem(MAX_RFIS);
        if (maxPageSize) {
            this.maxRfisPageSize = Number(maxPageSize);
        } else {
            localStorage.setItem(MAX_RFIS, `${this.maxRfisPageSize}`);
        }
    }

    get urlRoot() {
        if (this._urlRoot === "") {
            this._urlRoot = this.newformaApiClient.getHostNameWithProtocol();
        }
        return this._urlRoot;
    }

    get cacheDisabled() {
        try {
            const disabled = localStorage.getItem(CACHE_DISABLED);

            return disabled !== null;
        } catch {
            return false;
        }
    }

    saveCacheRfis(projectName: any, items: any, timeStamp = Date.now()) {
        const count = items.length;
        const accessed = DateHelper.timeSmall();
        const cacheData = { projectName, count, timeStamp, accessed, items };
        if (this._rifs.length > 10) {
            Log.info("Removing oldest submittals");
            this._rifs = this._rifs.slice(1); // drop the least used...
        }
        // put at end of array
        this._rifs.push(cacheData);

        // save to localstorage
        try {
            localStorage.setItem(CACHED_RFIS, JSON.stringify(this._rifs));
            this._wasCached = true;
        } catch {}
    }

    loadCacheRfis(projectName: string) {
        let cachedSubmittals;

        this.doAbort();

        try {
            this.purgeCacheRfis();
            if (this.cacheDisabled) {
                return [];
            }
            // check if a cache exists?
            const ix = this._rifs.findIndex((item: any) => item.projectName === projectName);
            if (ix >= 0) {
                cachedSubmittals = this._rifs[ix];
            }

            if (cachedSubmittals) {
                // remove at current position
                this._rifs.splice(ix, 1);
                // move to top, update the time
                this.saveCacheRfis(projectName, cachedSubmittals.items);
            } else {
                Log.info(`Submittals Cache not found for ${projectName}`);
            }
        } catch {
            Log.info("unknown error retrieving cached submittals");
        }

        return cachedSubmittals?.items;
    }

    cacheExists(): boolean {
        try {
            return localStorage.getItem(CACHED_RFIS) !== null;
        } catch (error) {
            return false;
        }
    }

    purgeCacheRfis() {
        Log.info("Purge Cache Rfis");
        if (this._wasCached) {
            Log.info("wasCached");
            if (!this.cacheExists()) {
                // the LS cache was manually deleted, to force reloading...
                Log.info("Cache doesn`t exist.");

                this._rifs = [];
                this._wasCached = false;
                return;
            }
        }

        const rifs = this._rifs;
        const oldestTimeStamp = Date.now() - this._expiredRfisMs;

        let size = this._rifs.length;
        let ix = 0;

        for (let i = 0; i < size; i++) {
            if (rifs[ix]?.timeStamp < oldestTimeStamp) {
                rifs?.splice(ix); // remove it
                size--;
            } else {
                ix++;
            }
        }
    }

    getNPCServer(nrn: string): string {
        if (nrn) {
            const parts = nrn.split(":");

            if (parts[0] === "nrn") {
                return parts[5];
            }
        }
        return "";
    }

    get AbortCount() {
        return this.abortCount;
    }

    async abortRequest() {
        this.requestWrapper.abortCurrentRequest();
    }

    checkAbort() {
        return new Promise((resolve, reject) => {
            const intervalId = setInterval(() => {
                if (!this.lock) {
                    clearInterval(intervalId);
                    resolve(false);
                }
            }, 100);

            setTimeout(() => {
                clearInterval(intervalId);
                reject(new Error("Timeout: Flag did not change to false within 2 seconds."));
            }, 2000);
        });
    }

    doAbort() {
        if (this.lock) {
            Log.infoStyle("Attempting to terminate", "red");

            this.abortCount = this.abortCount + 1;
            const result = this.checkAbort();
        }
    }

    clearCache() {
        // is there an external cache...  ?
        const isCached = localStorage.getItem(CACHED_RFIS);
        Log.info(`Cache Check ${this._wasCached}`);
        if (!isCached) {
            this._wasCached = false;
        } else {
            localStorage.setItem(CACHED_RFIS, "");
        }

        Log.infoStyle("Clearing Cache to resync", "red");
        this._rifs = [];
    }

    async fileEmailAsRfi(
        projectNrn: string,
        params: LogRfiParams,
        messageNrn: string,
        attachments: (AttachmentDetails | FileWithId)[],
        fileUploadCallback: (isInProgress: boolean, failedIds: string[]) => void,
        isCloudProject: boolean,
        isLastModifiedDateSupported: boolean,
        isForwardedRfi: boolean,
        culture?: string
    ): Promise<void> {
        this.analyticsManager.recordEvent(AnalyticsCategoryType.UserActions, AnalyticsActionType.FileEmailAsRfi);
        if (!!params.number) {
            this.analyticsManager.recordEvent(AnalyticsCategoryType.UserActions, AnalyticsActionType.RfiNumber);
        }

        let attachmentsDetails: AttachmentDetailsMetadata[] = [];
        Log.infoStyle(`${this.componentName}logEmailAsSubmittalReviewerResponse`, "red");
        if (attachments.length > 0) {
            attachmentsDetails = await this.outlookApiService.getFileAttachmentDetailsMetadata(
                attachments,
                isLastModifiedDateSupported
            );
            params.attachments = mapAttachmentsMetadataToAttachment(attachmentsDetails, isLastModifiedDateSupported);
        }

        let response: LogRfiResponse;

        try {
            response = await this.logRfi(projectNrn, params);
        } catch (error) {
            this.logger.error("RfiApiService. There was an error filing email as RFI", error);

            if ((error as any).status === 409) {
                throw new ApiRequestErrorWithMessage(
                    (error as any).message,
                    ApiRequestErrorLevel.ERROR,
                    "RFI.ERRORS.NUMBER_CONFLICT",
                    (error as any).status
                );
            }

            throw new ApiRequestErrorWithMessage(
                (error as any).message,
                ApiRequestErrorLevel.ERROR,
                isForwardedRfi ? "RFI.FORWARD_RFI.CREATE_FAILED" : "RFI.ERRORS.LOG_RFI_FAILED_GENERIC",
                (error as any).status
            );
        }

        if (attachments.length > 0) {
            const batchId = v4();
            const result = await this.fileUploadApiService.uploadWorkflowAttachments(
                attachmentsDetails,
                response.uploadFolderNrn,
                fileUploadCallback,
                batchId
            );
            fileUploadCallback(false, result.failedFileIds);
            if (result.failedFileIds.length > 0) {
                throw new ApiRequestErrorWithMessage(
                    "one or more files failed to upload",
                    ApiRequestErrorLevel.ERROR,
                    "RFI.ERRORS.FAILED_ATTACHMENTS_UPLOAD",
                    400
                );
            }
        }

        // if there are no attachments, then call forward rfi endpoint
        if (attachments.length === 0 && params.forwardRfiData) {
            await this.logEmailAsForwardRfi(
                params.forwardRfiData,
                projectNrn,
                response.nrn,
                attachments,
                messageNrn,
                isLastModifiedDateSupported
            );
        }

        const filingProjectNrn = isCloudProject ? projectNrn : response.nrn;
        await this.fileEmailToProject(filingProjectNrn, messageNrn, culture);
    }

    async logEmailAsForwardRfi(
        forwardRfi: LogForwardRfiRequest,
        projectNrn: string,
        rfiNrn: string,
        attachments: AttachmentItem[],
        messageNrn: string,
        isLastModifiedDateSupported: boolean
    ): Promise<LogForwardRfiResponse> {
        let attachmentsDetails: AttachmentDetailsMetadata[] = [];
        let forwardResponse;
        let forwardSuccess = false;
        let attempt = 0;

        function delay(ms: number) {
            return new Promise((resolve) => setTimeout(resolve, ms));
        }

        const forwardError: string | null = null;
        if (attachments.length > 0) {
            attachmentsDetails = await this.outlookApiService.getFileAttachmentDetailsMetadata(
                attachments,
                isLastModifiedDateSupported
            );
            forwardRfi.attachments = mapAttachmentsMetadataToAttachment(
                attachmentsDetails,
                isLastModifiedDateSupported
            );
        }
        while (attempt < this.maxRetries && !forwardSuccess) {
            try {
                attempt++;
                forwardResponse = await this.logForwardRfi(forwardRfi, projectNrn, rfiNrn);
                forwardSuccess = true;
            } catch (error) {
                this.logger.error(`RfiApiService - forward RFI attempt ${attempt}. Logging a forwarded RFI`, error);
                if (attempt >= this.maxRetries) {
                    forwardSuccess = false;
                    const handledErrors: { [index: string]: string } = {
                        403: "RFI.ERRORS.PERMISSIONS_INSUFFICIENT",
                        409: "RFI.ERRORS.NUMBER_CONFLICT",
                        500: "RFI.ERRORS.UNEXPECTED_ERROR",
                    };

                    const errorCode = (error as any).response?.status || 500;
                    const errorMessage = handledErrors[errorCode] || "RFI.ERRORS.UNEXPECTED_ERROR";

                    throw new Error(errorMessage);
                }
                await delay(this.retryDelay);
            }
        }
        const filingProjectNrn = rfiNrn;
        await this.fileEmailToProject(filingProjectNrn, messageNrn);
        return { forwardSuccess, forwardError };
    }

    async logEmailAsRfiReviewResponse(
        projectNrn: string,
        messageNrn: string,
        rfiNrn: string,
        params: RfiReviewResponseRequestParams,
        attachments: AttachmentItem[],
        fileUploadCallback: (isInProgress: boolean, failedIds: string[]) => void,
        isLastModifiedDateSupported: boolean,
        isRespondAndClose: boolean = false,
        respondCloseAttachments: AttachmentItem[] = [],
        culture?: string
    ): Promise<WorkflowActionResponse> {
        this.analyticsManager.recordEvent(
            AnalyticsCategoryType.UserActions,
            AnalyticsActionType.FileEmailAsRfiReviewResponse
        );
        Log.infoStyle(`logEmailAsRfiReviewResponse`, "red");
        let attachmentsDetails = await this.outlookApiService.getFileAttachmentDetailsMetadata(
            attachments,
            isLastModifiedDateSupported
        );
        params.attachments = mapAttachmentsMetadataToAttachment(attachmentsDetails, isLastModifiedDateSupported);
        const respondCloseAttachmentsDetails = await this.outlookApiService.getFileAttachmentDetailsMetadata(
            respondCloseAttachments,
            isLastModifiedDateSupported
        );

        if (params.respondCloseRfiData) {
            params.respondCloseRfiData.attachments = mapAttachmentsMetadataToAttachment(
                respondCloseAttachmentsDetails,
                isLastModifiedDateSupported
            );
        }

        attachmentsDetails = Array.from(
            new Map(
                [...(attachmentsDetails ?? []), ...respondCloseAttachmentsDetails].map((item) => [
                    JSON.stringify(item),
                    item,
                ])
            ).values()
        );

        let response: WorkflowActionResponse;
        try {
            response = await this.logRfiReviewerResponse(params, projectNrn, rfiNrn);
        } catch (error) {
            Log.error("RfiApiService. Logging a RFI Reviewer Response", error);

            const handledErrors: { [index: string]: string } = {
                403: "RFI.RESPONSE.UNAUTHORIZED_ERROR",
                409: "RFI.RESPONSE.FAILED_CONFLICT",
                500: isRespondAndClose ? "RFI.RESPOND_CLOSE.FAILED_GENERIC" : "RFI.RESPONSE.FAILED_GENERIC",
            };

            const messageToDisplay = handledErrors[(error as any).status];
            if (messageToDisplay) {
                throw new ApiRequestErrorWithMessage(
                    (error as any).message,
                    ApiRequestErrorLevel.ERROR,
                    messageToDisplay,
                    (error as any).status
                );
            }

            throw error;
        }

        if (attachments.length > 0 || respondCloseAttachments.length > 0) {
            const batchId = v4();
            const result = await this.fileUploadApiService.uploadWorkflowAttachments(
                attachmentsDetails,
                response.uploadFolderNrn,
                fileUploadCallback,
                batchId
            );
            fileUploadCallback(false, result.failedFileIds);
            if (result.failedFileIds.length > 0) {
                throw new ApiRequestErrorWithMessage(
                    "one or more files failed to upload",
                    ApiRequestErrorLevel.ERROR,
                    "RFI.RESPONSE.FAILED_ATTACHMENTS_UPLOAD",
                    400
                );
            }
        }
        const project = this.nrnServiceWrapper.isCloudProject(projectNrn) ? projectNrn : rfiNrn;
        await this.fileEmailToProject(project, messageNrn, culture);

        return response;
    }

    async getForwardedRfis(projectNrn: string, callback?: (value: number) => void): Promise<ForwardedRfi[]> {
        let abort = false;
        const startTime = Date.now();
        let forwardedRfis: ForwardedRfi[] = [];
        let items: ForwardedRfi[] = [];

        const isCloudProject = this.nrnServiceWrapper.isCloudProject(projectNrn);
        const filter = isCloudProject ? "forwarded" : undefined;
        let retry = 0;
        const aCount = this.abortCount;

        this.lock = true;

        try {
            let offsetToken: string | undefined;

            while (true) {
                try {
                    const result: any = await this.getRfis(projectNrn, this.maxRfisPageSize, filter, offsetToken);

                    // if abortCount changed while fetching, bail out
                    if (this.abortCount !== aCount) {
                        Log.infoStyle("Inflight getRfis terminating", "red");
                        abort = true;
                    } else {
                        items = items.concat(result.items);

                        if (callback) {
                            callback(items.length);
                        }

                        offsetToken = result.paging?.offsetToken;
                    }
                } catch (error) {
                    const statusCode = (error as any).status;

                    if (statusCode >= 500) {
                        // Server issue → reduce page size and retry
                        this.maxRfisPageSize = this.maxRfisPageSize - 10;
                        if (this.maxRfisPageSize <= 0) {
                            abort = true;
                        }
                        Log.infoStyle(`Failed to get RFIs. Retry with ${this.maxRfisPageSize}`, "red");
                    } else {
                        retry++;
                        if (retry > 3) {
                            Log.infoStyle(`Unknown error in getForwardedRfis [${statusCode}]`, "red");
                            this.doAbort();
                        }
                    }
                }

                if (abort || !offsetToken) {
                    break;
                }
            }

            if (!abort) {
                if (isCloudProject) {
                    forwardedRfis = items;
                } else {
                    forwardedRfis = items.filter(
                        (rfi) => rfi.status.type === "forwarded" || rfi.status.type === "open"
                    );
                }
            } else {
                Log.info("Aborting RFI loop");
            }
        } catch {
            Log.error("Error getting RFIs");
        } finally {
            Log.info(`End of RFIs fetch ${Date.now() - startTime} ms`);
            this.lock = false;
        }

        if (this.abortCount !== aCount) {
            forwardedRfis = undefined as any; // signal that it was aborted
            Log.infoStyle("Aborted RFIs", "red");

            if (callback) {
                callback(0); // reset counter
            }
        }

        return forwardedRfis;
    }

    async getRfiDetails(rfiNrn: string, projectNrn: string): Promise<RfiDetailsResponse> {
        const url = `${this.urlRoot}/v1/projects/${encodeURIComponent(projectNrn)}/rfis/${encodeURIComponent(rfiNrn)}`;

        const options: Request = {
            url: url,
            method: "GET",
            headers: {
                "x-newforma-agent": this.newformaApiClient.getNewformaAgent(),
            },
        };

        this.logger.info("Fetching RFI details");
        return this.newformaApiClient.makeRequest(options, (signedOptions) =>
            this.requestWrapper.get(signedOptions.url, undefined, signedOptions.headers, undefined)
        );
    }

    async getRfiActionKeywords(projectNrn: string, actionType: WorkflowActionType): Promise<ProjectKeywordsResponse> {
        const url = `${this.urlRoot}/v1/projects/${encodeURIComponent(
            projectNrn
        )}/rfis/workflowactions/${actionType}/keywords`;

        const options: Request = {
            url: url,
            method: "GET",
            headers: {
                "x-newforma-agent": this.newformaApiClient.getNewformaAgent(),
            },
        };

        this.logger.info(`Retrieving rfi keywords for action: ${actionType}`);
        return this.newformaApiClient.makeRequest(options, (signedOptions) =>
            this.requestWrapper.get(signedOptions.url, undefined, signedOptions.headers, undefined)
        );
    }

    private async logRfi(projectNrn: string, params: LogRfiParams): Promise<LogRfiResponse> {
        const options: Request = {
            url: `${this.urlRoot}/v1/projects/${projectNrn}/rfis/log`,
            method: "POST",
            body: JSON.stringify(params),
            headers: {
                "x-newforma-agent": this.newformaApiClient.getNewformaAgent(),
            },
        };
        this.logger.info("Logging a rfi");
        return this.newformaApiClient.makeRequest(options, (signedOptions) =>
            this.requestWrapper.post(signedOptions.url, undefined, signedOptions.headers, signedOptions.body)
        );
    }

    private async logForwardRfi(forwardRfi: LogForwardRfiRequest, projectNrn: string, rfiNrn: string): Promise<any> {
        const url = `${this.urlRoot}/v1/projects/${encodeURIComponent(projectNrn)}/rfis/${encodeURIComponent(
            rfiNrn
        )}/${WorkflowActionType.Forward.toLowerCase()}`;

        const options: Request = {
            url: url,
            method: "PUT",
            body: JSON.stringify(forwardRfi),
            headers: {
                "x-newforma-agent": this.newformaApiClient.getNewformaAgent(),
            },
        };

        this.logger.info("Logging a FORWARDED RFI");
        return this.newformaApiClient.makeRequest(options, (signedOptions) =>
            this.requestWrapper.put(signedOptions.url, undefined, signedOptions.headers, signedOptions.body)
        );
    }

    private async logRfiReviewerResponse(
        reviewerResponse: RfiReviewResponseRequestParams,
        projectNrn: string,
        rfiNrn: string
    ): Promise<WorkflowActionResponse> {
        const url = `${this.urlRoot}/v1/projects/${encodeURIComponent(projectNrn)}/rfis/${encodeURIComponent(
            rfiNrn
        )}/${WorkflowActionType.ReviewResponse.toLocaleLowerCase()}`;
        const options: Request = {
            url: url,
            method: "PUT",
            body: JSON.stringify(reviewerResponse),
            headers: {
                "x-newforma-agent": this.newformaApiClient.getNewformaAgent(),
            },
        };

        this.logger.info("Logging a rfi reviewer response");
        return this.newformaApiClient.makeRequest(options, (signedOptions) =>
            this.requestWrapper.put(signedOptions.url, undefined, signedOptions.headers, signedOptions.body)
        );
    }

    private async fileEmailToProject(
        projectNrn: string,
        messageNrn: string,
        culture?: string
    ): Promise<PostEmailFilingResponse> {
        this.logger.info("Logging an rfi - Filing email");

        try {
            return await this.emailApiService.fileToProject(projectNrn, messageNrn, culture);
        } catch (error) {
            this.logger.error("RfiApiService. Logging an rfi - Filing email", error);

            if (ExpiredSessionError.isInstanceOf(error)) {
                throw error;
            }

            throw new ApiRequestErrorWithMessage(
                (error as any).message,
                ApiRequestErrorLevel.ERROR,
                "RFI.ERRORS.FILING_EMAIL_FAILED",
                (error as any).status
            );
        }
    }

    async getAllRfis(projectNrn: string, callback?: (value: number) => void, allowContractManager: boolean = false) {
        let items: any = [];
        let retry = 0;
        let count = 0;
        let offsetToken;

        let abort = false;

        Log.info(`${this.componentName} get All Rfis for project [ ${projectNrn} ] Lock: ${this.lock} `);

        // experience timeouts at 800, and 600 so reducing further
        let maxPageSize = Number(this.maxRfisPageSize);

        const aCount = this.abortCount;
        this.lock = true;
        // future task - make this simpler
        while (true) {
            try {
                const result: any = await this.getRfis(
                    projectNrn,
                    maxPageSize,
                    undefined,
                    offsetToken,
                    allowContractManager
                );

                const itsDone = result.items < maxPageSize && result.items > 0;
                Log.infoStyle("It Returned", "red");

                // any change in the global count, results in this data thrown away
                if (this.abortCount !== aCount && !itsDone) {
                    Log.infoStyle("Inflight getAllRfis terminating", "red");
                    abort = true;
                } else {
                    items = items.concat(result.items);

                    if (callback) {
                        callback(items.length);
                    }

                    offsetToken = result.paging?.offsetToken;
                }
            } catch (error) {
                const statusCode = (error as any).status;

                if (statusCode >= 500) {
                    // its a timeout, caused by a server failure (insufficent resources - solution increase mem from 512mb on server)
                    // reduce the page size and try again
                    maxPageSize = maxPageSize - 100;
                    if (maxPageSize <= 0) {
                        abort = true;
                    }
                    Log.infoStyle(`Failed to get rfis. Retry with ${maxPageSize}`);
                } else {
                    retry++;
                    if (retry > 3) {
                        Log.infoStyle(`An unknown error occured code = [${statusCode}] `);
                        this.doAbort();
                    }
                }
            }

            if (abort || !offsetToken) {
                break;
            } else {
                count++;
                Log.info(`Rfis Page [${count}]: Sum: ${items.length} ${offsetToken ?? ""}`);
            }
        }
        return items;
    }

    private async getRfis(
        projectNrn: string,
        maxPageSize: number,
        filter: string = "",
        offsetToken: string = "",
        allowContractManager: boolean = false
    ): Promise<RfiResponse> {
        const url = `${this.urlRoot}/v1/projects/${projectNrn}/rfis?status=${filter}&maxSize=${maxPageSize}&offsetToken=${offsetToken}&allowContractManager=${allowContractManager}`;
        const options: Request = {
            url: url,
            method: "GET",
            headers: {
                "x-newforma-agent": this.newformaApiClient.getNewformaAgent(),
            },
        };

        this.logger.info("Retrieving rfis");
        return this.newformaApiClient.makeRequest(options, (signedOptions) =>
            this.requestWrapper.get(signedOptions.url, undefined, signedOptions.headers, undefined)
        );
    }
}
